import { useCallback, useEffect, useState } from 'react';

import type { VListHandle } from 'virtua';

type Options = {
  ids: string[];
  virtualizer: VListHandle | null;
};

export function useVirtualizedScrollToIds({ ids, virtualizer }: Options) {
  const [scrollToIds, setScrollToIds] = useState<string[]>([]);

  const scrollToElement = useCallback(
    (segmentIds: string[]) => {
      if (!virtualizer) {
        return;
      }

      const scrolledToIndex = ids.indexOf(segmentIds[0]);
      if (scrolledToIndex >= 0) {
        virtualizer.scrollToIndex(scrolledToIndex, {
          align: 'nearest',
        });
      }

      if (segmentIds.length === 1) {
        return;
      }

      const scrolledToIndexLast = ids.findIndex(
        (id) => id === segmentIds[segmentIds.length - 1],
      );
      if (scrolledToIndexLast >= 0) {
        // Need to postpone to the next tick to allow the scroll to happen, otherwise seems like
        // virtua combines scrollToIndex calls and only one is applied.
        setTimeout(() => {
          virtualizer.scrollToIndex(scrolledToIndexLast, {
            align: 'nearest',
          });
        }, 0);
      }
    },
    [ids, virtualizer],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: JSON.stringify(scrollToIds) is used to compare arrays
  useEffect(() => {
    if (scrollToIds.length === 0) {
      return;
    }
    if (
      !scrollToIds.every((scrollToId) => ids.some((id) => id === scrollToId))
    ) {
      return;
    }
    scrollToElement(scrollToIds);
    setScrollToIds([]);
  }, [scrollToElement, JSON.stringify(scrollToIds), ids]);

  return { setScrollToIds };
}
